import { cn } from "@/lib/utils";
import type React from "react";
import { useState, useEffect } from "react";

type AnimatedInputPlaceholderProps = {
  prefix?: string; // Static prefix for the placeholder
  placeholders?: string[]; // Array of dynamic placeholder texts to cycle through
  duration?: number; // Time (in seconds) each placeholder remains before changing
  className?: string; // Additional CSS classes for the input
  inputClassName?: string; // Additional CSS classes for the input element
  // Other props (type, name, etc.) passed to the input element
} & React.InputHTMLAttributes<HTMLInputElement>;

function AnimatedInputPlaceholder({
  prefix = "",
  placeholders = [],
  duration = 3,
  className = "",
  inputClassName = "",
  ...props
}: AnimatedInputPlaceholderProps) {
  const [index, setIndex] = useState(0); // Current index in the placeholders array
  const [isActive, setIsActive] = useState(true); // Whether to show and cycle the placeholder
  const [fading, setFading] = useState(false); // Whether the placeholder text is mid-fade

  // Cycle through placeholder texts at the given interval with fade transition
  useEffect(() => {
    if (!isActive || placeholders.length === 0) return;
    // Set up interval to change placeholder every `duration` seconds
    const cycleInterval = setInterval(() => {
      // Trigger fade-out
      setFading(true);
      // After a short delay, switch to the next placeholder and fade-in
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % placeholders.length);
        setFading(false);
      }, 500); // 500ms fade duration (adjustable as needed)
    }, duration * 1000);

    return () => clearInterval(cycleInterval); // Cleanup on unmount or when isActive changes
  }, [isActive, placeholders, duration]);

  // Event handlers to manage placeholder visibility on focus/blur
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsActive(false); // Pause cycling and hide placeholder when focused
    if (props.onFocus) props.onFocus(e); // Preserve any external onFocus handler
  };
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value === "") {
      setIsActive(true); // Resume placeholder if input is empty on blur
    }
    if (props.onBlur) props.onBlur(e); // Preserve any external onBlur handler
  };

  return (
    <div className={cn("relative", className)}>
      {/* Text Input */}
      <input
        {...props}
        type="text"
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={inputClassName}
        /*
          Note: We intentionally leave out a placeholder attribute here because we
          will render a custom placeholder element for animation. If you prefer
          using the native placeholder attribute, you could set it to
          `${prefix} ${placeholders[index]}` and use Tailwind focus utilities
          like `focus:placeholder-opacity-0` to hide it on focus [oai_citation_attribution:2‡stackoverflow.com](https://stackoverflow.com/questions/77513168/flowbite-react-textinput-placeholder-disappear-on-focus#:~:text=input%3Afocus,out%20%21important%3B).
        */
      />

      {/* Animated Placeholder Text (only shown when input is not focused/has no value) */}
      {isActive && (
        <span
          aria-hidden="true" // Mark as decorative; screen readers can rely on input's aria-label if provided
          className={`pointer-events-none absolute inset-0 flex items-center
                      pl-2 text-faded transition-opacity duration-500
                      ${fading ? "opacity-0" : "opacity-100"}`}
        >
          {prefix}&nbsp;
          <span className="font-thin">{placeholders[index]}</span>
        </span>
      )}
    </div>
  );
}

export default AnimatedInputPlaceholder;
